<template>
    <div ref="root" class="rich-text-editor" :style="{ border: '1px solid #ddd' }" />
</template>

<script>
import { createUEConfig, getToolbarByType } from '@/utils/editorConfig'
const loadQueue = []
let resourcesReady = false
function injectStyle(href) {
    if (document.querySelector(`link[href="${href}"]`)) return
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    document.head.appendChild(link)
}

function injectScript(src, onLoad, onError) {
    const script = document.createElement('script')
    script.src = src
    script.onload = onLoad
    script.onerror = onError
    document.head.appendChild(script)
}
function loadResources(callback) {
    if (resourcesReady) {
        callback()
        return
    }
    const BASE = '/百度富文本编辑器ueditor/ueditor-master/'
    injectStyle(BASE + 'themes/default/css/ueditor.css')
    injectStyle(BASE + 'themes/default/css/ueditor.min.css')
    window.UEDITOR_HOME_URL = BASE
    injectScript(
        BASE + 'ueditor.config.js',
        () => injectScript(
            BASE + 'ueditor.all.min.js',
            () => {
                resourcesReady = true
                callback()
            },
            () => {
                console.error('[RichTextEditor] 加载 ueditor.all.min.js 失败')
                callback()
            }
        ),
        () => {
            console.error('[RichTextEditor] 加载 ueditor.config.js 失败')
            callback()
        }
    )
}
function sanitizeHtml(html) {
    if (!html) return html
    try {
        html = html.replace(/<img[^>]*>/gi, match => {
            const src = match.match(/src\s*=\s*["']?([^"'\s>]+)/i)?.[1] || ''
            return src.startsWith('file:') ? '' : match
        })
        html = html.replace(
            /(style|src|href|background|data|sizes)=["'][^"']*file:[^"']*["']/gi,
            ''
        )
        html = html.replace(/<(o|v|w|win):[^>]*>[\s\S]*?<\/\1:[^>]*>/gi, '')
        html = html.replace(/<(o|v|w|win):[^>]*\/?>/gi, '')
        html = html.replace(/<o:p[^>]*>/gi, '')

        return html
    } catch {
        return html
    }
}
function applyUETablePatch() {
    if (!window.UE?.UETable) return
    const proto = window.UE.UETable.prototype
    if (proto.__patched) return
    proto.__patched = true

    proto.getMaxRows = function () {
        const rows = this.table?.rows
        if (!rows?.length) return 1
        return Array.from(rows).reduce((max, row) => {
            const maxColSpan = Array.from(row.cells).reduce((m, c) => Math.max(m, c.rowSpan || 1), 1)
            return Math.max(maxColSpan + row.rowIndex, max)
        }, 1)
    }

    proto.getMaxCols = function () {
        const rows = this.table?.rows
        if (!rows?.length) return 1
        const rowSpans = {}
        return Array.from(rows).reduce((max, row, i) => {
            let count = 0
            Array.from(row.cells).forEach(cell => {
                count += cell.colSpan || 1
                if (cell.rowSpan > 1) {
                    for (let r = 1; r < cell.rowSpan; r++) {
                        rowSpans[`row_${i + r}`] = (rowSpans[`row_${i + r}`] || 0) + (cell.colSpan || 1)
                    }
                }
            })
            return Math.max(count + (rowSpans[`row_${i}`] || 0), max)
        }, 0)
    }
}
let instanceId = 0
export default {
    name: 'RichTextEditor',

    props: {
        /** v-model 绑定的 HTML 字符串 */
        value: { type: String, default: '' },
        /** 编辑器初始高度（px） */
        height: { type: Number, default: 400 },
        /** placeholder */
        placeholder: { type: String, default: '请输入内容...' },
        /** 只读模式 */
        disabled: { type: Boolean, default: false },
        /** 上传配置透传（imageUrl 等） */
        uploadConfig: { type: Object, default: () => ({}) },
        /** 调试：在控制台打印 getContent() 输出 */
        debugLog: { type: Boolean, default: false },
    },

    data: () => ({
        editor: null,
        uid: `ueditor-${++instanceId}`,
        destroying: false,
        _pasteHandler: null,
    }),

    computed: {
        config() {
            return createUEConfig({
                height: this.height,
                placeholder: this.placeholder,
                readonly: this.disabled,
                ...this.uploadConfig,
            })
        },
    },

    watch: {
        value(html) {
            if (this.destroying) return
            const current = this.getContent()
            if (html !== current && html !== current + '<br>') {
                this._setContent(html)
            }
        },
    },

    mounted() {
        this.$nextTick(this._init)
    },

    beforeDestroy() {
        this.destroying = true
        this._teardown()
    },

    methods: {
        async _init() {
            const el = this.$refs.root
            if (!el) return
            this._bindPaste(el)
            await new Promise(resolve => loadResources(resolve))
            if (this.destroying) return
            applyUETablePatch()
            const script = document.createElement('script')
            script.id = this.uid
            script.name = 'content'
            script.type = 'text/plain'
            el.appendChild(script)

            try {
                this.editor = window.UE.getEditor(this.uid, this.config)
            } catch (err) {
                console.error('[RichTextEditor] 初始化失败:', err)
                return
            }

            this.editor.addListener('ready', () => {
                if (this.destroying) {
                    this.editor.destroy()
                    return
                }
                applyUETablePatch()
                const initial = this.pendingContent ?? sanitizeHtml(this.value)
                this.pendingContent = null
                if (initial) {
                    this._setContent(initial)
                }
                this.editor.addListener('contentChange', () => {
                    if (!this.destroying) {
                        const html = this.getContent()
                        if (this.debugLog) {
                            console.log('[RichTextEditor] contentChange:', html)
                        }
                        this.$emit('input', html)
                    }
                })
                if (this.disabled) {
                    this.editor.setDisabled()
                }
            })
        },
        _teardown() {
            if (this._pasteHandler) {
                this.$refs.root?.removeEventListener('paste', this._pasteHandler, true)
                this._pasteHandler = null
            }
            document.getElementById(this.uid)?.remove()
            if (this.editor) {
                try { this.editor.blur() } catch { }
                try { this.editor.destroy() } catch (e) {
                    console.warn('[RichTextEditor] 销毁失败:', e)
                }
                this.editor = null
            }
        },
        _bindPaste(el) {
            const handler = (e) => {
                const clipboard = e.clipboardData ?? window.clipboardData
                if (!clipboard) return

                const rawHtml = clipboard.getData('text/html')
                if (!rawHtml) return
                const safeHtml = /file:\/\//i.test(rawHtml)
                    ? sanitizeHtml(rawHtml)
                    : rawHtml
                e.preventDefault()
                try {
                    this.editor?.execCommand('insertHtml', safeHtml, true)
                } catch (err) {
                    console.warn('[RichTextEditor] 粘贴插入失败:', err)
                }
            }

            el.addEventListener('paste', handler, true)
            this._pasteHandler = handler
        },
        _setContent(html) {
            if (!this.editor) return
            const safe = sanitizeHtml(html)
            try {
                this.editor.body.innerHTML = safe || ''
                this.editor.fireEvent('contentChange')
            } catch (e) {
                console.warn('[RichTextEditor] _setContent 失败:', e)
            }
        },
        clear() {
            this._setContent('')
        },
        getContent() {
            if (!this.editor) return ''
            try { return this.editor.getContent() } catch { return '' }
        },
        setContent(html) {
            if (!html) return this.clear()
            this._setContent(html)
        },
        insertHtml(html) {
            if (!this.editor) return
            try {
                this.editor.execCommand('insertHtml', html, true)
            } catch (e) {
                console.warn('[RichTextEditor] insertHtml 失败:', e)
            }
        },
        getText() {
            if (!this.editor) return ''
            try { return this.editor.getContentTxt() } catch { return '' }
        },
    },
}
</script>

<style lang="less" scoped>
.rich-text-editor {
    border-radius: 4px;
    line-height: normal;
    overflow: hidden;
    min-height: 200px;
    max-height: 50vh;
    overflow-y: auto;
}
</style>

<style lang="less">
.edui-editor-body .edui-body-container {
    font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', 'SimSun', serif;
    font-size: 16px;
    line-height: 1.8;
    color: #333;
    letter-spacing: 0.5px;
    padding: 16px 20px;
}
.edui-editor-body .edui-body-container p {
    margin: 0;
    margin-bottom: 12px;
    line-height: 1.8;
    text-indent: 2em;
    &:first-child {
        text-indent: 2em;
    }
}
.edui-editor-body .edui-body-container h1 {
    font-size: 26px;
    font-weight: 700;
    margin: 0 0 16px;
    line-height: 1.4;
    text-indent: 0;
    color: #1a1a1a;
}

.edui-editor-body .edui-body-container h2 {
    font-size: 22px;
    font-weight: 700;
    margin: 0 0 14px;
    line-height: 1.4;
    text-indent: 0;
    color: #1a1a1a;
}

.edui-editor-body .edui-body-container h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 12px;
    line-height: 1.5;
    text-indent: 0;
    color: #1a1a1a;
}

.edui-editor-body .edui-body-container h4,
.edui-editor-body .edui-body-container h5,
.edui-editor-body .edui-body-container h6 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 10px;
    line-height: 1.5;
    text-indent: 0;
    color: #1a1a1a;
}
.edui-editor-body .edui-body-container ul,
.edui-editor-body .edui-body-container ol {
    margin: 0 0 12px;
    padding-left: 2.5em;
    line-height: 1.8;
}

.edui-editor-body .edui-body-container ul {
    list-style-type: disc;
}

.edui-editor-body .edui-body-container ol {
    list-style-type: decimal;
}

.edui-editor-body .edui-body-container li {
    margin: 4px 0;
    line-height: 1.8;
    padding-left: 4px;
    text-indent: 0;

    p {
        margin: 0;
        text-indent: 0;
    }
}
.edui-editor-body .edui-body-container blockquote {
    margin: 0 0 12px;
    padding: 8px 16px;
    border-left: 4px solid #c0c0c0;
    background: #f8f8f8;
    color: #555;
    text-indent: 0;
}
.edui-editor-body .edui-body-container pre {
    margin: 0 0 12px;
    padding: 12px 16px;
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-family: 'Courier New', Consolas, monospace;
    font-size: 14px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-all;
    text-indent: 0;
}

.edui-editor-body .edui-body-container code {
    padding: 2px 6px;
    background: #f0f0f0;
    border-radius: 3px;
    font-family: 'Courier New', Consolas, monospace;
    font-size: 14px;
    color: #c7254e;
}
.edui-editor-body .edui-body-container hr {
    margin: 16px 0;
    border: none;
    border-top: 1px solid #d0d0d0;
}
.edui-editor-body .edui-body-container a {
    color: #2a5caa;
    text-decoration: underline;
}
.edui-editor-body table {
    border-spacing: 0;
    margin: 0 0 12px;
    font-size: 14px;
}

.edui-editor-body table col,
.edui-editor-body table colgroup col {
    width: auto !important;
}

.edui-editor-body table td,
.edui-editor-body table th {
    padding: 6px 10px;
    vertical-align: middle;
    min-width: 40px;
    word-break: break-word;
    text-indent: 0;
}
.edui-editor-body .edui-body-container img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 8px auto;
    border-radius: 2px;
}
.edui-editor-body .edui-body-container span[style*="mso-"],
.edui-editor-body .edui-body-container div[style*="mso-"],
.edui-editor-body .edui-body-container p[style*="mso-"],
.edui-editor-body .edui-body-container font,
.edui-editor-body .edui-body-container o\:p {
    font-size: inherit !important;
    font-family: inherit !important;
    color: inherit !important;
    letter-spacing: inherit !important;
    line-height: inherit !important;
    vertical-align: baseline !important;
}
</style>
